"use client";

import * as React from "react";
import { ArrowRight, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Credibility() {
  const [activeFilter, setActiveFilter] = React.useState<"all" | "new">("all");
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSearchOpen(false);
    };
    if (searchOpen) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [searchOpen]);
  return <section data-section-id="3960" id="institutional-credibility" className="bg-slate-950 text-white relative min-h-[80vh] w-full overflow-hidden">
      {}
      <div className="relative z-20 flex items-center justify-between px-6 pt-8 md:px-12 lg:px-16">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
          BENEWEALTH / Private Real Estate Capital Investor
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} aria-label="Search resources" className="rounded-lg border border-slate-800 bg-slate-900/40 backdrop-blur-sm hover:bg-slate-800/60">
            <Search className="size-4" />
          </Button>
          <CartIcon variant="ghost" size="icon" iconSize="size-4" className="rounded-lg border border-slate-800 bg-slate-900/40 backdrop-blur-sm hover:bg-slate-800/60" />
        </div>
      </div>

      {}
      <div className="relative grid grid-cols-1 gap-12 px-6 pt-12 pb-20 md:px-12 lg:grid-cols-12 lg:gap-0 lg:px-16 lg:pt-16">
        {}
        <div aria-hidden="true" className="pointer-events-none absolute top-12 bottom-20 left-[58.333%] hidden w-px lg:block" style={{
        background: "linear-gradient(to bottom, transparent 0%, hsl(45 100% 50%) 20%, hsl(45 100% 50%) 80%, transparent 100%)",
        boxShadow: "0 0 12px hsl(45 100% 50%)"
      }} />

        {}
        <div ref={leftRef} className={`lg:col-span-7 lg:pr-16 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="flex items-center gap-3 font-mono text-[11px] uppercase text-amber-500" style={{
          letterSpacing: "0.24em"
        }}>
            <span className="inline-block size-1.5 rounded-lg" style={{
            background: "hsl(45 100% 50%)",
            boxShadow: "0 0 8px hsl(45 100% 50%)"
          }} />
            <span>Architect of Capital — Phase 01</span>
          </div>

          {}
          <h1 className="mt-10 font-default font-bold uppercase leading-[0.85] text-white" style={{
          fontSize: "clamp(72px, 14vw, 200px)",
          letterSpacing: "-0.04em"
        }}>
            <span className="block">The</span>
            <span className="block">
              Vault
              <span style={{
              color: "hsl(45 100% 50%)",
              textShadow: "0 0 24px hsl(45 100% 50%)"
            }}>
                .
              </span>
            </span>
          </h1>

          {}
          <p className="mt-10 max-w-xl font-default text-slate-300" style={{
          fontSize: "22px",
          lineHeight: 1.5
        }}>
            Private capital provider for experienced real estate investment entities, delivering tailored solutions to accelerate investor growth.
          </p>

          {}
          <div className="mt-14 flex flex-wrap items-center gap-3">
            <Button variant="ghost" onClick={() => setActiveFilter("all")} className={`h-10 rounded-lg px-5 font-mono text-[11px] uppercase backdrop-blur-sm transition-all ${activeFilter === "all" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white"}`} style={{
            letterSpacing: "0.2em",
            border: activeFilter === "all" ? "1px solid hsl(45 100% 50%)" : "1px solid hsl(222 47% 22%)",
            boxShadow: activeFilter === "all" ? "0 0 16px hsl(45 100% 50% / 0.25)" : undefined
          }}>
              Get Pre-Qualified
            </Button>
            <Button asChild variant="ghost" className={`h-10 rounded-lg px-5 font-mono text-[11px] uppercase backdrop-blur-sm transition-all ${activeFilter === "new" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white"}`} style={{
            letterSpacing: "0.2em",
            border: activeFilter === "new" ? "1px solid hsl(45 100% 50%)" : "1px solid hsl(222 47% 22%)",
            boxShadow: activeFilter === "new" ? "0 0 16px hsl(45 100% 50% / 0.25)" : undefined
          }}>
              <Link to="/PreQualificationFormPage">Get Pre-Qualified</Link>
            </Button>
          </div>

          {}
          <p className="mt-8 font-default text-sm text-slate-500">
            Direct capital deployment, no-fluff approach.
          </p>
        </div>

        {}
        <div ref={rightRef} className={`lg:col-span-5 lg:pl-16 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            {}
            <div className="mb-6 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              <span>Engine / 001</span>
              <span style={{
              color: "hsl(45 100% 50%)"
            }}>—</span>
            </div>

            {}
            <div className="relative aspect-[3/4] w-full overflow-hidden bg-slate-900" style={{
            boxShadow: "0px 60px 100px -20px hsl(0 0% 0% / 0.8), 0px 30px 60px -30px hsl(0 0% 0% / 0.6)"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/vault_architecture_blueprint.webp" alt="Architectural blueprint of a high-rise development project" loading="lazy" decoding="async" className="h-full w-full object-cover opacity-80" />
              {}
              <div className="pointer-events-none absolute top-3 left-3 size-3 border-t border-l border-white/20" />
              <div className="pointer-events-none absolute top-3 right-3 size-3 border-t border-r border-white/20" />
              <div className="pointer-events-none absolute bottom-3 left-3 size-3 border-b border-l border-white/20" />
              <div className="pointer-events-none absolute bottom-3 right-3 size-3 border-b border-r border-white/20" />
            </div>

            {}
            <div className="mt-6 flex items-end justify-between gap-4">
              <div>
                <p className="font-default text-white" style={{
                fontSize: "16px"
              }}>
                  Capital Deployment
                </p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                  First Mortgage & Gap
                </p>
              </div>
              <div className="flex items-center gap-2 font-mono text-[11px] text-white">
                <span>DEPLOYED</span>
                <ArrowRight className="size-3" style={{
                color: "hsl(45 100% 50%)"
              }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="relative z-10 flex items-center justify-between border-t border-slate-800 px-6 py-5 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 md:px-12 lg:px-16">
        <span>Est. MMXXIV</span>
        <span className="hidden md:inline">Architect of Capital</span>
        <span style={{
        color: "hsl(45 100% 50%)"
      }}>● Active</span>
      </div>

      {}
      {searchOpen && <div className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 backdrop-blur-xl">
          <div className="flex items-center justify-between border-b border-slate-800 px-6 py-6 md:px-12 lg:px-16">
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-400">
              Search BeneWealth
            </span>
            <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} aria-label="Close search" className="rounded-lg">
              <X className="size-5" />
            </Button>
          </div>
          <div className="flex flex-1 items-start justify-center px-6 pt-16 md:px-12 lg:px-16">
            <div className="w-full max-w-2xl">
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search loan programs, JV criteria, referral partners..." className="w-full" />
              <p className="mt-6 font-default text-sm text-slate-500">
                Press <span className="font-mono">ESC</span> to close.
              </p>
            </div>
          </div>
        </div>}
    </section>;
}